import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Warning as WarningIcon } from '@material-ui/icons';
import { Loader } from '../../atoms';
import { useGetAllInspectorsQuery, useUpdateInspectionInspectorMutation } from '../../redux/rtkQuery/apiSlice';
import { ModalDefaultDraggable } from '../../molecules';
import { getTradesByProjectType } from '../../api/inspections';
import { getEffectiveTradeIds, TradeRef } from '../../utils/inspectorTradeQualification';

const InspectorDropdown = ({ row }) => {
  const { data = [], isFetching } = useGetAllInspectorsQuery(null);
  const [updateInspectionInspector, { isLoading }] = useUpdateInspectionInspectorMutation();
  const [inspectorValue, setInspectorValue] = useState<string>();
  const [pendingChange, setPendingChange] = useState<{ value: string; label: string } | null>(null);

  // EA-1124 parity with EA-1195: qualifying an inspector must account for the
  // Residential-license expansion AND the project type (1&2 dwelling vs
  // multifamily/commercial), not just a raw trade intersection. Load the
  // trades valid for this inspection's project type.
  const [projectTypeTrades, setProjectTypeTrades] = useState<TradeRef[]>([]);
  useEffect(() => {
    let cancelled = false;
    getTradesByProjectType(row?.projectType || null)
      .then((res: any) => {
        if (!cancelled) setProjectTypeTrades((res?.data?.trades as TradeRef[]) || []);
      })
      .catch(() => {
        if (!cancelled) setProjectTypeTrades([]);
      });
    return () => {
      cancelled = true;
    };
  }, [row?.projectType]);

  const inspectionTrades: number[] = (row?.trades || []).map((t: any) => Number(t));
  const qualifiedInspectors = React.useMemo(() => {
    if (!inspectionTrades.length) return data;
    const inspectionSet = new Set<number>(inspectionTrades);
    const projectSet = new Set<number>((projectTypeTrades || []).map(t => Number(t.id)));
    return data.filter((insp: any) => {
      const effIds = getEffectiveTradeIds(insp.trade, projectTypeTrades);
      return effIds.some((id: number) => {
        // Gate on the project-type trade set when we have one (a bare
        // Residential license can't cover a commercial-only trade).
        if (projectSet.size && !projectSet.has(id)) return false;
        return inspectionSet.has(id);
      });
    });
  }, [data, inspectionTrades, projectTypeTrades]);
  // Always include the currently-assigned inspector so the dropdown reflects
  // reality even if they're no longer qualified.
  const options = React.useMemo(() => {
    const current = data.find((d: any) => d.label === row.inspectorName);
    if (current && !qualifiedInspectors.some((i: any) => i.value === current.value)) {
      return [current, ...qualifiedInspectors];
    }
    return qualifiedInspectors;
  }, [data, qualifiedInspectors, row.inspectorName]);

  useEffect(() => {
    const selected = data.find(d => row.inspectorName === d.label);
    if (selected) {
         setInspectorValue(selected.value);
    }
  }, [data, row]);

  const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    const selectedInspector = data.find(d => d.value === newValue);
    if (selectedInspector) {
      setPendingChange({ value: newValue, label: selectedInspector.label });
    }
  };

  const onConfirm = async () => {
    if (!pendingChange) return;
    setInspectorValue(pendingChange.value);
    setPendingChange(null);
    await updateInspectionInspector({
      id: row.id,
      inspector: Number(pendingChange.value),
      title: row.title,
      inspection_type: row.inspectionTypeId,
      status: row.status,
    });
  };

  const onCancel = () => {
    setPendingChange(null);
  };

  return (
    <Wrapper>
      <StyledSelect value={inspectorValue} onChange={onChange}>
        {options.map((item, index) => (
          <option key={index} value={item.value}>
            {item.label}
          </option>
        ))}
      </StyledSelect>
      <LoaderWrapper>{isFetching || (isLoading && <Loader />)}</LoaderWrapper>
      {pendingChange && (
        <ModalDefaultDraggable
          title={<TitleWithIcon><WarningIcon style={{ color: '#f5a623', fontSize: 28 }} /> Change Inspector Confirmation</TitleWithIcon>}
          onClose={onCancel}
          centered
        >
          <ConfirmContent>
            <p>You are about to change the assigned inspector to {pendingChange.label}.</p>
            <ButtonsRow>
              <SecondaryButton type="button" onClick={onCancel}>
                Cancel
              </SecondaryButton>
              <PrimaryButton type="button" onClick={onConfirm}>
                Update
              </PrimaryButton>
            </ButtonsRow>
          </ConfirmContent>
        </ModalDefaultDraggable>
      )}
    </Wrapper>
  );
};

export default InspectorDropdown;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${FONT_STACK};
`;

const StyledSelect = styled.select`
  width: 140px;
  height: 40px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--neutral-0);
  color: var(--mainText);
  font-size: 0.875rem;
  font-family: ${FONT_STACK};
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--neutral-500);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }

  @media only screen and (max-width: 540px) {
    width: 100%;
    max-width: 200px;
  }
`;

const LoaderWrapper = styled.div`
  zoom: 0.35;
  position: relative;
  top: 5px;
  left: 5px;
`;

const TitleWithIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ConfirmContent = styled.div`
  text-align: center;
  font-family: ${FONT_STACK};
  p {
    margin: 16px 0 24px;
    font-size: 0.875rem;
    color: var(--mainText);
  }
`;

const ButtonsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;

  @media only screen and (max-width: 540px) {
    width: 100%;

    button {
      flex: 1;
      min-width: 0;
    }
  }
`;

const PrimaryButton = styled.button`
  min-width: 140px;
  height: 40px;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-weight: 600;
  font-size: 0.875rem;
  font-family: ${FONT_STACK};
  text-transform: none;
  letter-spacing: 0.01em;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const SecondaryButton = styled.button`
  min-width: 140px;
  height: 40px;
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-weight: 600;
  font-size: 0.875rem;
  font-family: ${FONT_STACK};
  text-transform: none;
  letter-spacing: 0.01em;
  border: 1px solid var(--border);
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-500);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;
